
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useUpgradePayment, Payment } from '@/api/payments';
import { getPlans } from '@/api/plans';
import { Checkbox } from '@/components/ui/checkbox';

interface UpgradePaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: Payment;
  onSuccess?: () => void;
}

export function UpgradePaymentDialog({
  open,
  onOpenChange,
  payment,
  onSuccess,
}: UpgradePaymentDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [plans, setPlans] = useState<any[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const upgradeMutation = useUpgradePayment();

  // Get current payment amount
  const currentPaymentAmount = payment.amount || 0;
  const currentPaymentCurrency =
    typeof payment.planId === 'object' && payment.planId?.currency
      ? payment.planId.currency
      : 'ALL';

  // Filter plans by search query
  const filteredPlans = plans.filter((plan) =>
    plan.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    if (open) {
      setIsLoadingPlans(true);
      getPlans()
        .then((data) => {
          // Filter out the current plan
          const currentPlanId =
            typeof payment.planId === 'object' && payment.planId?._id
              ? payment.planId._id
              : payment.planId;
          const filteredPlans = data.filter(
            (plan: any) => plan._id !== currentPlanId
          );
          setPlans(filteredPlans);
        })
        .catch((error) => {
          console.error('Error fetching plans:', error);
          toast({
            title: t('common.error'),
            description: 'Failed to load plans',
            variant: 'destructive',
          });
        })
        .finally(() => {
          setIsLoadingPlans(false);
        });
    }
  }, [open, payment, t, toast]);

  const handleUpgrade = async () => {
    if (!selectedPlanId) {
      toast({
        title: t('common.error'),
        description: 'Please select a plan',
        variant: 'destructive',
      });
      return;
    }

    try {
      await upgradeMutation.mutateAsync({
        paymentId: payment._id!,
        newPlanId: selectedPlanId,
      });

      toast({
        title: t('common.success'),
        description: t('payments.upgraded', 'Payment upgraded successfully'),
      });

      onOpenChange(false);
      setSelectedPlanId('');

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error upgrading payment:', error);
      toast({
        title: t('common.error'),
        description: t('payments.upgradeError', 'Failed to upgrade payment'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t('payments.upgradePayment', 'Upgrade Plan')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current Plan Info */}
          <div className="bg-muted p-4 rounded-lg">
            <div className="text-sm font-medium mb-2">
              {t('payments.currentPayment', 'Current Payment')}
            </div>
            <div className="text-lg font-bold">
              {currentPaymentAmount} {currentPaymentCurrency}
            </div>
          </div>

          {isLoadingPlans ? (
            <div className="text-center py-8">{t('common.loading')}</div>
          ) : plans.length === 0 ? (
            <div className="text-center py-8">
              {t('payments.noPlansAvailableForUpgrade', 'No plans available for upgrade')}
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>{t('payments.selectNewPlan', 'Select New Plan')}</Label>
                <Input
                  type="text"
                  placeholder={t('plans.searchPlans', 'Search plans...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {filteredPlans.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {t('plans.noPlansFound', 'No plans found')}
                  </div>
                ) : (
                  filteredPlans.map((plan) => {
                    const priceDifference = plan.price - currentPaymentAmount;
                    const isLowerPrice = plan.price < currentPaymentAmount;

                    return (
                      <div
                        key={plan._id}
                        className={`flex items-start space-x-3 p-3 border rounded-lg ${
                          isLowerPrice
                            ? 'opacity-50 cursor-not-allowed bg-muted'
                            : 'hover:bg-accent cursor-pointer'
                        }`}
                        onClick={() =>
                          !isLowerPrice && setSelectedPlanId(plan._id)
                        }
                      >
                        <Checkbox
                          checked={selectedPlanId === plan._id}
                          disabled={isLowerPrice}
                          onCheckedChange={(checked) => {
                            if (!isLowerPrice) {
                              if (checked) {
                                setSelectedPlanId(plan._id);
                              } else {
                                setSelectedPlanId('');
                              }
                            }
                          }}
                        />
                        <div className="flex-1">
                          <div className="font-medium">{plan.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {plan.price} {plan.currency} - {plan.duration}{' '}
                            {plan.durationType}
                          </div>
                          {!isLowerPrice && (
                            <div className="text-sm font-medium text-green-600 mt-1">
                              {t('payments.toPay', 'To pay')}: +
                              {priceDifference} {plan.currency}
                            </div>
                          )}
                          {isLowerPrice && (
                            <div className="text-sm font-medium text-red-600 mt-1">
                              {t(
                                'payments.cannotDowngrade',
                                'Cannot downgrade to lower price'
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              setSelectedPlanId('');
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleUpgrade}
            disabled={!selectedPlanId || upgradeMutation.isPending}
          >
            {upgradeMutation.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
